<script lang="ts">
  import { intersection } from "@opera-gx/common/src/actions/intersection";
  import { Player } from "@opera-gx/components/v6";
  import { onDestroy, onMount } from "svelte";

  import Tile from "../../components/Tile/Tile.svelte";
  import { isIos } from "../../env";
  import { activeIframe, videoTitle } from "../../store/frame";
  import Placeholder from "../Placeholder.svelte";

  export let url;
  export let lazy = true;
  export let title: string;
  export let index: number;
  export let width: number | null = null;
  export let height: number | null = null;
  export let short = false;
  export let thumbnail: string | null = null;
  let intersecting = false;
  let loaded = false;
  let loadingIframe = true;
  const parseUrl = JSON.parse(url);

  const videoThumbnail = thumbnail
    ? thumbnail
    : parseUrl.thumbnail.replace("hqdefault.jpg", "mqdefault.jpg");

  let youtube_embed: string;

  $: if (parseUrl) {
    const videoUrl = new URL(parseUrl.url);
    let id = videoUrl.searchParams.get("v");

    if (id === undefined || id === null) {
      id = parseUrl.url.split("/").pop();
    }

    youtube_embed = `https://www.youtube.com/embed/${id}?controls=0&enablejsapi=1&origin=${window.location.href}&autoplay=1`;
  }
  const handleLoad = () => {
    loaded = true;
  };

  const handleIframeLoaded = () => {
    loadingIframe = false;
  };

  let showPlaceholder = false;
  let timeout: NodeJS.Timeout;
  onMount(() => {
    timeout = setTimeout(() => {
      showPlaceholder = true;
    }, 1000);
  });
  onDestroy(() => {
    clearTimeout(timeout);
  });

  const getTileGradientDirection = (index: number) => (index % 2 ? "tl" : "br");

  const toggleActive = () => {
    $activeIframe = true;
    $videoTitle = title;
    loadingIframe = true;
  };
  function handleGlobalClick(event) {
    const clickedOnVideo = event.target.closest(".wrapper");

    if (clickedOnVideo) {
      $activeIframe = false;
      $videoTitle = "";
    }
  }
  onMount(() => {
    document.body.addEventListener("click", handleGlobalClick);
  });

  onDestroy(() => {
    document.body.removeEventListener("click", handleGlobalClick);
  });
</script>

<style lang="postcss">
  .fader {
    opacity: 0;
    transition: opacity 0.5s ease-out;
    width: 100%;
  }

  .visible {
    opacity: 1;
  }

  .short {
    aspect-ratio: 9/16;
    object-fit: cover;
  }

  .wrapper {
    user-select: none;
    color: white;
    transition:
      opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0;
    transform: translateX(-15px);
    will-change: opacity, transform;

    &.show {
      opacity: 1;
      transform: translateX(0);
    }

    &:active {
      transform: scale(1.04);
    }
  }

  .video-wrapper {
    position: relative;
    color: var(--hsl-primary-text);
  }

  .title {
    position: absolute;
    width: 80vw;
    top: 15px;
    left: 15px;
  }

  .thumb {
    height: 100%;
    width: 100%;
  }

  .player-wrapper {
    position: relative;
  }

  .placeholder {
    position: absolute;
    z-index: 1;
    width: 100%;
  }

  .play-outer {
    background-color: rgb(46 39 63 / 60%);
    border-radius: 50%;
    padding: 18px;
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .play-inner {
    display: block;
    width: 0;
    height: 0;
    border-top: 17px solid transparent;
    border-left: 36px solid white;
    border-bottom: 17px solid transparent;
    margin-right: -6px;
  }
</style>

<!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
<div
  on:click={toggleActive}
  class="wrapper"
  class:show={intersecting && (loaded || showPlaceholder)}
  style="transition-delay: {40 * index}ms;"
  use:intersection={{
    handler: (entries) => {
      if (!entries.some(({ isIntersecting }) => isIntersecting)) {
        intersecting = false;

        if ($activeIframe && !isIos && $videoTitle === title) {
          $activeIframe = false;
        }

        return;
      }

      if (entries.some(({ intersectionRatio }) => intersectionRatio >= 0.05)) {
        intersecting = true;
        return;
      }
    },
    rootMargin: "100% 0px 0px 0px",
    threshold: [0, 0.05],
  }}>
  {#if $videoTitle === title && $activeIframe}
    <div class="player-wrapper">
      {#if loadingIframe}
        <div class="placeholder" class:short>
          <Placeholder variant={short ? "daily-short" : "video"} tiles={1} />
        </div>
      {/if}
      <Player
        src={youtube_embed}
        {title}
        {height}
        {width}
        legacyLoader={true}
        on:iframeLoaded={handleIframeLoaded} />
    </div>
  {:else}
    <Tile {lazy} cut={20} let:class={tile}>
      <div
        class={`${tile} bg-gradient-to-${getTileGradientDirection(
          index,
        )} to-accent/40 from-transparent after:bg-gradient-to-${getTileGradientDirection(
          index,
        )} after:to-accent
    after:from-transparent after:via-transparent
    `}>
        {#if videoThumbnail}
          <img
            loading={lazy ? "lazy" : "eager"}
            src={videoThumbnail}
            alt=""
            class="fader thumb"
            class:short
            class:visible={loaded}
            on:load|once={handleLoad} />
        {/if}
        {#if !short}
          <div class="title text-shadow text-white">{title}</div>
        {/if}
        <span class="play-outer">
          <span class="play-inner"></span>
        </span>
      </div>
    </Tile>
  {/if}
</div>
